import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import {
  buildAutoTaggingPlanSubmitItem,
  updateAutoTaggingPlan,
} from "../../../services/autoTaggingService";
import type { ConceptValue } from "../../../services/regulationsService";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import DiscardEnhancedTaggingRuleChangesDialog from "../EnhancedTaggingRule/DiscardEnhancedTaggingRuleChangesDialog";
import AssessmentPlanFilterFields from "./AssessmentPlanFilterFields";
import { ASSESSMENT_PROMPT_MAX_LENGTH } from "./AssessmentPromptField";
import AssessmentPromptFieldWithDefault from "./AssessmentPromptFieldWithDefault";
import ConceptValuesMultiSelect from "./ConceptValuesMultiSelect";
import type { TaggingPlanRow } from "../constants";
import {
  assertEditComboAvailability,
  buildPlanLimitToastMessage,
  isConceptPlanLimitError,
  type ConceptPlanLimitItem,
} from "./conceptStatsValidation";
import {
  hasDraftItemFieldErrors,
  PLAN_LIMIT_CONCEPT_ERROR,
  validateDraftItem,
  type DraftItemFieldErrors,
} from "./validateDraftItem";

type EditFormState = {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValues: ConceptValue[];
  assessmentPrompt: string;
  allValues: boolean;
  active: boolean;
};

function conceptValuesFingerprint(values: ConceptValue[]): string {
  return [...values]
    .map((value) => value.code)
    .sort((a, b) => a.localeCompare(b))
    .join("\0");
}

/** Compares editable fields against the values loaded when the dialog opened. */
export function isEditFormDirty(
  current: EditFormState,
  initial: EditFormState,
): boolean {
  return (
    current.objectSubType !== initial.objectSubType ||
    current.conceptCode !== initial.conceptCode ||
    current.allValues !== initial.allValues ||
    current.assessmentPrompt !== initial.assessmentPrompt ||
    conceptValuesFingerprint(current.conceptValues) !==
      conceptValuesFingerprint(initial.conceptValues)
  );
}

type EditAssessmentPlanItemDialogProps = Readonly<{
  open: boolean;
  plan: TaggingPlanRow | null;
  saving?: boolean;
  onClose: () => void;
  onSave: () => void;
  onSavingChange?: (saving: boolean) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}>;

function mapRowConceptValues(
  values: TaggingPlanRow["conceptValues"],
): ConceptValue[] {
  return values.map((value) => ({
    id: value.code,
    code: value.code,
    name: value.name.trim() || value.code,
    description: value.description,
  }));
}

function buildFormStateFromPlan(plan: TaggingPlanRow): EditFormState {
  return {
    objectType: plan.objectType,
    objectSubType: plan.objectSubType,
    conceptCode: plan.conceptCode,
    conceptValues: mapRowConceptValues(plan.conceptValues),
    assessmentPrompt: plan.description,
    allValues: plan.allValues,
    active: plan.enabled,
  };
}

function toComboDraftItem(
  id: string,
  item: Pick<EditFormState, "objectType" | "objectSubType" | "conceptCode">,
) {
  return {
    id,
    objectType: item.objectType,
    objectSubType: item.objectSubType,
    conceptCode: item.conceptCode,
  };
}

export default function EditAssessmentPlanItemDialog({
  open,
  plan,
  saving = false,
  onClose,
  onSave,
  onSavingChange,
  onError,
  onSuccess,
}: EditAssessmentPlanItemDialogProps) {
  const { primaryColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const [formState, setFormState] = useState<EditFormState | null>(null);
  const [initialFormState, setInitialFormState] = useState<EditFormState | null>(
    null,
  );
  const [fieldErrors, setFieldErrors] = useState<DraftItemFieldErrors>({});
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const { conceptOptions, getObjectSubTypeOptions, defaultObjectType } =
    useAssessmentPlanFilterOptions(
      formState?.objectType ?? plan?.objectType,
      formState?.objectSubType ?? plan?.objectSubType,
    );

  useEffect(() => {
    if (!open || !plan) {
      setFormState(null);
      setInitialFormState(null);
      setFieldErrors({});
      setCheckingLimit(false);
      setDiscardConfirmOpen(false);
      return;
    }

    const next = buildFormStateFromPlan(plan);
    setFormState(next);
    setInitialFormState(next);
    setFieldErrors({});
    setCheckingLimit(false);
    setDiscardConfirmOpen(false);
  }, [open, plan]);

  const updateFormState = useCallback(
    (patch: Partial<EditFormState>) => {
      setFormState((current) => (current ? { ...current, ...patch } : current));
      setFieldErrors((current) => {
        const next = { ...current };
        if (patch.objectType !== undefined) delete next.objectType;
        if (patch.objectSubType !== undefined) delete next.objectSubType;
        if (patch.conceptCode !== undefined) delete next.conceptCode;
        if (patch.conceptValues !== undefined || patch.allValues !== undefined) {
          delete next.conceptValues;
        }
        if (patch.assessmentPrompt !== undefined) delete next.assessmentPrompt;
        return next;
      });
    },
    [],
  );

  // AC2: when Sub Object is empty and the Object has subtypes, default to the first
  // (e.g. Insight → Regulation). Keep the baseline in sync so defaulting is not "dirty".
  useEffect(() => {
    if (!open || !formState?.objectType || formState.objectSubType.trim()) return;
    const options = getObjectSubTypeOptions(formState.objectType);
    const defaultSubType = options[0]?.code ?? "";
    if (!defaultSubType) return;
    updateFormState({ objectSubType: defaultSubType });
    setInitialFormState((current) =>
      current && !current.objectSubType.trim()
        ? { ...current, objectSubType: defaultSubType }
        : current,
    );
  }, [
    formState?.objectSubType,
    formState?.objectType,
    getObjectSubTypeOptions,
    open,
    updateFormState,
  ]);

  const isDirty = useMemo(() => {
    if (!formState || !initialFormState) return false;
    return isEditFormDirty(formState, initialFormState);
  }, [formState, initialFormState]);

  const handleRequestClose = useCallback(() => {
    if (saving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [isDirty, onClose, saving]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const handlePlanLimitReached = useCallback(
    (items: ConceptPlanLimitItem[], toastMessage?: string) => {
      if (items.length === 0) return;

      showMessage(toastMessage ?? buildPlanLimitToastMessage(items), "error");
      updateFormState({
        conceptCode: "",
        conceptValues: [],
        assessmentPrompt: "",
        allValues: false,
      });
      setFieldErrors((current) => ({
        ...current,
        conceptCode: PLAN_LIMIT_CONCEPT_ERROR,
      }));
    },
    [showMessage, updateFormState],
  );

  const checkComboLimit = useCallback(
    async (next: EditFormState) => {
      if (!plan || !next.conceptCode.trim()) return;

      setCheckingLimit(true);
      try {
        await assertEditComboAvailability(
          toComboDraftItem(plan.id, next),
          toComboDraftItem(plan.id, plan),
          defaultObjectType,
        );
      } catch (error) {
        if (isConceptPlanLimitError(error)) {
          handlePlanLimitReached(error.items, error.message);
        } else {
          showMessage(
            error instanceof Error
              ? error.message
              : "Failed to check plan availability.",
            "warning",
          );
        }
      } finally {
        setCheckingLimit(false);
      }
    },
    [defaultObjectType, handlePlanLimitReached, plan, showMessage],
  );

  const handleSave = useCallback(async () => {
    if (!plan || !formState || saving || checkingLimit) return;

    const assessmentPrompt = formState.assessmentPrompt.trim();
    const validationTarget = {
      objectType: formState.objectType,
      objectSubType: formState.objectSubType,
      conceptCode: formState.conceptCode,
      conceptValues: formState.conceptValues,
      assessmentPrompt,
      allValues: formState.allValues,
    };
    const errors = validateDraftItem(validationTarget, {
      requireObjectSubType:
        getObjectSubTypeOptions(formState.objectType).length > 0,
    });
    if (hasDraftItemFieldErrors(errors)) {
      setFieldErrors(errors);
      return;
    }

    onSavingChange?.(true);
    try {
      await assertEditComboAvailability(
        toComboDraftItem(plan.id, formState),
        toComboDraftItem(plan.id, plan),
        defaultObjectType,
      );

      const payload = buildAutoTaggingPlanSubmitItem({
        objectType: formState.objectType,
        objectSubType: formState.objectSubType,
        conceptCode: formState.conceptCode,
        conceptValues: formState.allValues ? [] : formState.conceptValues,
        assessmentPrompt,
        allValues: formState.allValues,
        active: formState.active,
      });

      await updateAutoTaggingPlan(plan.id, payload);
      onSuccess?.("Item saved successfully.");
      onSave();
    } catch (error) {
      if (isConceptPlanLimitError(error)) {
        handlePlanLimitReached(error.items, error.message);
      } else {
        onError?.(
          error instanceof Error
            ? error.message
            : "Failed to update tagging plan.",
        );
      }
    } finally {
      onSavingChange?.(false);
    }
  }, [
    checkingLimit,
    defaultObjectType,
    formState,
    getObjectSubTypeOptions,
    handlePlanLimitReached,
    onError,
    onSave,
    onSavingChange,
    onSuccess,
    plan,
    saving,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(handleRequestClose)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#262626" }}
            >
              Edit Assessment Plan Item
            </Typography>
            <IconButton
              aria-label="Close"
              onClick={handleRequestClose}
              disabled={saving}
              sx={{
                ...DIALOG_CLOSE_BUTTON_SX,
                position: "absolute",
                right: 16,
                top: 16,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {formState ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <AssessmentPlanFilterFields
                variant="form"
                fullWidth
                objectTypeDisabled
                objectSubTypeDisabled
                objectType={formState.objectType}
                objectSubType={formState.objectSubType}
                conceptCode={formState.conceptCode}
                errors={{
                  objectType: fieldErrors.objectType,
                  objectSubType: fieldErrors.objectSubType,
                  conceptCode: fieldErrors.conceptCode,
                }}
                onObjectTypeChange={(nextObjectType, nextObjectSubType) =>
                  updateFormState({
                    objectType: nextObjectType,
                    objectSubType: nextObjectSubType,
                    conceptCode: "",
                    conceptValues: [],
                    allValues: false,
                  })
                }
                onObjectSubTypeChange={(nextObjectSubType) =>
                  updateFormState({
                    objectSubType: nextObjectSubType,
                    conceptCode: "",
                    conceptValues: [],
                    allValues: false,
                  })
                }
                onConceptCodeChange={(nextConceptCode) => {
                  const nextState: EditFormState = {
                    ...formState,
                    conceptCode: nextConceptCode,
                    conceptValues: [],
                    // AC3: first select of Tag Dimension defaults Tag Values to All.
                    allValues: Boolean(nextConceptCode.trim()),
                  };
                  updateFormState({
                    conceptCode: nextConceptCode,
                    conceptValues: [],
                    allValues: Boolean(nextConceptCode.trim()),
                  });
                  void checkComboLimit(nextState);
                }}
              />

              <ConceptValuesMultiSelect
                key={`${plan?.id ?? "edit"}-${formState.conceptCode}`}
                conceptCode={formState.conceptCode}
                conceptName={
                  conceptOptions.find(
                    (option) => option.code === formState.conceptCode,
                  )?.name
                }
                value={formState.conceptValues}
                allValues={formState.allValues}
                error={Boolean(fieldErrors.conceptValues)}
                helperText={fieldErrors.conceptValues}
                onChange={({
                  conceptValues: nextValues,
                  allValues: nextAllValues,
                }) => {
                  updateFormState({
                    conceptValues: nextValues,
                    allValues: nextAllValues,
                  });
                }}
              />

              <AssessmentPromptFieldWithDefault
                conceptCode={formState.conceptCode}
                objectType={formState.objectType}
                objectSubType={formState.objectSubType}
                value={formState.assessmentPrompt}
                error={Boolean(fieldErrors.assessmentPrompt)}
                helperText={fieldErrors.assessmentPrompt}
                maxLength={ASSESSMENT_PROMPT_MAX_LENGTH}
                onChange={(nextValue) =>
                  updateFormState({ assessmentPrompt: nextValue })
                }
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleRequestClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderColor: primaryColor,
              color: primaryColor,
              "&:hover": {
                borderColor: primaryColor,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || checkingLimit || !formState}
            sx={{
              ...brandContainedButtonSx,
              bgcolor: `${primaryColor} !important`,
              "&:hover": {
                bgcolor: `${primaryColor} !important`,
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DiscardEnhancedTaggingRuleChangesDialog
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
}
